/**
 * Phase 6c PR 5 — `<PinnedContext />`.
 *
 * Promotes the mobile-only `<ContextDrawer>` pill into a permanent
 * rail card on `/v2/today` at Ultra (≥ 1920 px). Surfaces "what your
 * coaches know" (body / shoes / nutrition / mental headlines) as a
 * persistent presence in the right rail instead of a buried drawer.
 *
 * Per direction.md §3.7:
 *   "The mobile-only ContextDrawer pill becomes a rail card on Ultra:
 *    body / shoes / nutrition / mental headlines + edit link.
 *    Information that was buried gets a permanent home."
 *
 * The component is a pure presentation shell — caller passes a
 * `ContextItem[]` (same shape as ContextDrawer) so the data source
 * (real `useAthleteContext()` or fixture for design preview) is
 * decided upstream.
 *
 * Visibility: parent wraps in a `display-gated` slot
 * (`@media (min-width: 1920px)`) — the DOM mounts at all tiers but
 * the card only paints at Ultra.
 */

import type { ContextItem } from '../ContextDrawer/ContextDrawer';
import styles from './PinnedContext.module.css';

export interface PinnedContextProps {
  items: ContextItem[];
  /** Optional "updated 2d" right-hand label in the kicker row. */
  updatedLabel?: string;
  /** Optional href for the footer link. */
  editHref?: string;
  /** Optional click handler for the footer link (overrides editHref). */
  onEdit?: () => void;
  /** Footer link label. F-513 — call sites pass "What Airo knows ›" so the
   *  affordance says what its destination is (a read-only view), not "Edit".
   *  Defaults to the historical "Edit context ›". */
  editLabel?: string;
}

export function PinnedContext({
  items,
  updatedLabel,
  editHref,
  onEdit,
  editLabel = 'Edit context ›',
}: PinnedContextProps) {
  if (items.length === 0) return null;

  return (
    <section className={styles.pinnedCtx} aria-label="Pinned coach context">
      <div className={styles.kicker}>
        <span>Pinned context</span>
        {updatedLabel && <span className={styles.updated}>{updatedLabel}</span>}
      </div>
      <div className={styles.ctxList}>
        {items.map((item) => (
          <div key={item.label} className={styles.ctxRow}>
            <span className={styles.k}>{item.label}</span>
            <span
              className={styles.v}
              style={item.muted ? { color: 'var(--ink-400)' } : undefined}
            >
              {item.value}
            </span>
          </div>
        ))}
      </div>
      {onEdit ? (
        <button type="button" className={styles.editLink} onClick={onEdit}>
          {editLabel}
        </button>
      ) : (
        <a className={styles.editLink} href={editHref ?? '#'}>
          {editLabel}
        </a>
      )}
    </section>
  );
}

export default PinnedContext;
